/**
 * Brand Info rows for a material slug.
 * Exact slug only, plus goat/slot variants (`kid-{slug}`, `slot-{slug}`).
 * Do not treat the last word as a family: `rave` must not pull `antique-rave`.
 */
const VARIANT_PREFIXES = ["kid-", "slot-"];

export function slugMatchesMaterial(itemSlug, materialSlug) {
  const item = String(itemSlug || "")
    .trim()
    .toLowerCase();
  const needle = String(materialSlug || "")
    .trim()
    .toLowerCase();
  if (!item || !needle) {
    return false;
  }
  if (item === needle) {
    return true;
  }
  return VARIANT_PREFIXES.some((prefix) => item === `${prefix}${needle}`);
}

export function getRowCustomerBrand(item) {
  return String(item?.brand || "")
    .trim()
    .toLowerCase();
}

export function getBrandDataForSlug(brandRows, slug, brand) {
  if (!slug || !Array.isArray(brandRows)) {
    return [];
  }

  const needle = String(slug).trim().toLowerCase();
  if (!needle) {
    return [];
  }

  const brandNeedle = String(brand || "")
    .trim()
    .toLowerCase();

  return brandRows.filter((item) => {
    if (!slugMatchesMaterial(item?.slug, needle)) {
      return false;
    }

    if (!brandNeedle) {
      return true;
    }

    const itemBrand = String(item?.brand || "")
      .trim()
      .toLowerCase();
    return !itemBrand || itemBrand === brandNeedle;
  });
}

export function collectBrandArticleSlugs(brandRows, brand) {
  const slugs = new Set();
  if (!Array.isArray(brandRows)) {
    return slugs;
  }

  const brandNeedle = String(brand || "")
    .trim()
    .toLowerCase();

  brandRows.forEach((item) => {
    const slug = String(item?.slug || "")
      .trim()
      .toLowerCase();
    if (!slug) {
      return;
    }

    if (brandNeedle) {
      const itemBrand = String(item?.brand || "")
        .trim()
        .toLowerCase();
      if (itemBrand) {
        if (itemBrand !== brandNeedle) {
          return;
        }
      } else if (brandNeedle !== "nike") {
        return;
      }
    }

    slugs.add(slug);
  });

  return slugs;
}

export function materialHasBrandArticles(brandSlugSet, slug) {
  if (!brandSlugSet || brandSlugSet.size === 0) {
    return false;
  }

  const needle = String(slug || "")
    .trim()
    .toLowerCase();
  if (!needle) {
    return false;
  }

  if (brandSlugSet.has(needle)) {
    return true;
  }

  return VARIANT_PREFIXES.some((prefix) => brandSlugSet.has(`${prefix}${needle}`));
}

export function getBrandMaterialName(item, fallbackTitle = "") {
  return String(
    item?.material_name || item?.pdm_material_name || fallbackTitle || "",
  ).trim();
}

export function formatBrandMaterialName(item, fallbackTitle = "") {
  return getBrandMaterialName(item, fallbackTitle).toUpperCase();
}

export function getBrandId(item) {
  const value = item?.brandId ?? item?.pcx_material_number ?? item?.ref;
  if (value == null || value === "") {
    return null;
  }
  return value;
}

export function getBrandIdKind(item) {
  const kind = String(item?.brandIdKind || item?.ref_kind || "")
    .trim()
    .toLowerCase();
  if (kind === "mlm") {
    return "mlm";
  }
  return "pcx";
}

export function formatBrandId(item) {
  const id = getBrandId(item);
  if (id == null) {
    return null;
  }
  return getBrandIdKind(item) === "mlm" ? `MLM# ${id}` : `PCX# ${id}`;
}

export function formatBrandMeta(item) {
  return [
    item?.thickness != null && item.thickness !== ""
      ? `${item.thickness}mm`
      : null,
    item?.status ? String(item.status).trim() : null,
    formatBrandId(item),
  ]
    .filter(Boolean)
    .join(" · ");
}
